import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Popover } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { STORAGE_KEY } from '../../contexts/StorageContext';
import { CardType } from '../../types/card';
import type { FilterOption } from '../../hooks/UseCardView';
import { FONT_SIZE, FONT_WEIGHT, RADIUS, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone } from '../../hooks/UseIsCompact';

const RECENT_LIMIT = 3;

type TypeFilter = 'ALL' | 'BOOSTER' | 'STARTER';

function recentKey(gameType: CardType | 'ALL'): string {
  return `${STORAGE_KEY.expansionRecent}:${gameType}`;
}

function loadRecent(gameType: CardType | 'ALL'): string[] {
  try {
    const raw = localStorage.getItem(recentKey(gameType));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function saveRecent(gameType: CardType | 'ALL', codes: string[]): void {
  localStorage.setItem(recentKey(gameType), JSON.stringify(codes));
}

/** 코드 패밀리(그룹) 접두사 — 앞쪽 알파벳만(SV11B→SV, M2A→M, EXK-10→EXK, OP14→OP, P→P). */
const prefixOf = (v: string): string => v.match(/^[A-Za-z]+/)?.[0] ?? v;
/** 그룹 내 정렬 키 — 코드에서 처음 나오는 수(SV11B→11, EXK-10→10, LMK-1.0→1). */
const numOf = (v: string): number => parseFloat(v.match(/\d+(\.\d+)?/)?.[0] ?? '0') || 0;

interface ExpansionFilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  /** 최근 선택 목록을 게임별로 분리해서 저장하기 위한 스코프 (typeFilter 그대로 넘기면 됨). */
  gameType: CardType | 'ALL';
  /** pricehub 카탈로그를 불러오는 중일 때(UseExpansionCatalog) 목록 대신 로딩 문구를 보여준다. */
  loading?: boolean;
  /**
   * "확장팩 전체"를 고를 수 있는 목록 필터(가격/재고 관리)면 true(기본값). 신상품 등록처럼
   * 반드시 확장팩 하나를 정확히 골라야 하는 화면은 false로 꺼서 "전체" 항목 자체를
   * 없앤다(2026-08-27, 상품 등록 페이지 통합).
   */
  allowAll?: boolean;
  /** allowAll=false일 때 아직 아무것도 안 골랐을 때 트리거에 보여줄 문구. */
  placeholder?: string;
  /**
   * 부스터팩/스타터덱 종류 필터 칩(2026-08-31, Claude Design "확장팩 드롭다운 분류"). 기본
   * true지만, 옵션에 productType 정보가 하나도 없으면(로컬 파생 목록 등) 자동으로 숨는다.
   */
  showTypeFilter?: boolean;
  /** 코드 그룹 헤더를 클릭해 접을 수 있게 한다(2026-08-31). 기본 true. */
  collapsible?: boolean;
  style?: React.CSSProperties;
}

const PRODUCT_TYPE_LABEL: Record<'BOOSTER' | 'STARTER', string> = { BOOSTER: '부스터팩', STARTER: '스타터덱' };

function formatMeta(o: FilterOption): string | null {
  const parts: string[] = [];
  if (o.count != null) parts.push(`${o.count.toLocaleString()}장`);
  if (o.releaseDate) parts.push(o.releaseDate);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * 확장팩 필터 — 검색형 콤보박스(2026-08-27, Claude Design "확장팩 필터 개선" 도입).
 *
 * 기존 antd Select는 옵션이 60개를 넘는 게임(포켓몬 등)에서 이름을 다 기억하고 있어야
 * 스크롤로 찾을 수 있었다. 코드(SV11B)/이름(블랙볼트) 어느 쪽으로 쳐도 바로 좁혀지는
 * 검색창과, 자주 오가는 확장팩을 다시 찾지 않도록 최근 선택 3개를 맨 위에 보여준다.
 * ↑↓/Enter/Esc로 마우스 없이도 조작 가능.
 *
 * 2026-08-31 "확장팩 드롭다운 분류": 검색·최근·키보드 이동은 그대로 두고, ① 부스터팩/
 * 스타터덱 종류 필터 칩과 ② 코드 접두사(SV/M/EXK…)별 그룹 헤더(접기 가능)를 얹었다.
 *
 * 최근 선택은 게임(gameType)별로 localStorage에 분리 저장한다 — 포켓몬에서 고른 코드가
 * 원피스 목록에 섞여 나오면 의미가 없기 때문(StorageContext.STORAGE_KEY.expansionRecent 참고).
 */
export const ExpansionFilterSelect: React.FC<ExpansionFilterSelectProps> = ({
  value, onChange, options, gameType, loading,
  allowAll = true, placeholder, showTypeFilter = true, collapsible = true, style,
}) => {
  const t = useThemeTokens();
  // 휴대폰 폭(≤640) — 팝오버를 화면 안 폭으로, 줄 · 칩 · 접기 · 검색 · 열기 버튼을 44px 누르는 곳으로(코덱 화면 점검 2026-10-30, 감사 #518).
  const phone = useIsPhone();
  const TAP = phone ? 44 : undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [recent, setRecent] = useState<string[]>(() => loadRecent(gameType));
  const inputRef = useRef<InputRef>(null);

  useEffect(() => { setRecent(loadRecent(gameType)); }, [gameType]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    setTypeFilter('ALL');
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const selected = options.find((o) => o.value === value) ?? null;

  const anyHasType = useMemo(() => options.some((o) => o.productType), [options]);
  const typeFilterOn = showTypeFilter && anyHasType;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      if (typeFilterOn && typeFilter !== 'ALL' && o.productType !== typeFilter) return false;
      if (!q) return true;
      return o.value.toLowerCase().includes(q) || o.label.toLowerCase().includes(q);
    });
  }, [options, query, typeFilter, typeFilterOn]);

  // 코드 접두사별 그룹 — 접두사 오름차순, 그룹 내 코드 번호 오름차순.
  const groups = useMemo(() => {
    const byPrefix = new Map<string, FilterOption[]>();
    for (const o of filtered) {
      const p = prefixOf(o.value);
      const arr = byPrefix.get(p);
      if (arr) arr.push(o); else byPrefix.set(p, [o]);
    }
    return Array.from(byPrefix.keys()).sort().map((prefix) => ({
      prefix,
      items: byPrefix.get(prefix)!.slice().sort(
        (a, b) => (numOf(a.value) - numOf(b.value)) || a.value.localeCompare(b.value),
      ),
    }));
  }, [filtered]);

  // 키보드 이동용 평탄 목록 — 접힌 그룹의 항목은 뺀다.
  const flatValues = useMemo(() => {
    const acc: string[] = [];
    for (const g of groups) {
      if (!(collapsible && collapsed[g.prefix])) acc.push(...g.items.map((o) => o.value));
    }
    return acc;
  }, [groups, collapsed, collapsible]);
  const flatIndexOf = useMemo(() => {
    const m = new Map<string, number>();
    flatValues.forEach((v, i) => m.set(v, i));
    return m;
  }, [flatValues]);

  const recentOptions = useMemo(
    () => recent
      .map((code) => options.find((o) => o.value === code))
      .filter((o): o is FilterOption => !!o)
      .slice(0, RECENT_LIMIT),
    [recent, options],
  );

  const pick = (v: string) => {
    onChange(v);
    if (v !== 'ALL') {
      const next = [v, ...recent.filter((c) => c !== v)].slice(0, RECENT_LIMIT);
      setRecent(next);
      saveRecent(gameType, next);
    }
    setOpen(false);
  };

  const toggleGroup = (prefix: string) =>
    setCollapsed((c) => ({ ...c, [prefix]: !c[prefix] }));

  // allowAll일 때만 내비게이션 인덱스 0이 "확장팩 전체"이고, 그 뒤(1..)가 flatValues다.
  const allOffset = allowAll ? 1 : 0;
  const navCount = flatValues.length + allOffset;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 그룹 접기 · 최근 버튼에서 누른 Enter / Space 는 그 버튼 몫 — 목록의 강조 줄을 고르지 않는다(감사 #518).
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as HTMLElement).tagName === 'BUTTON') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, navCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allowAll && highlight === 0) pick('ALL');
      else {
        const v = flatValues[highlight - allOffset];
        if (v) pick(v);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const label = selected
    ? `${selected.value} · ${selected.label}`
    : loading
      ? '확장팩 불러오는 중...'
      : allowAll ? `확장팩 전체 (${options.length})` : (placeholder ?? '확장팩 선택');

  const totalCards = options.reduce((sum, o) => sum + (o.count ?? 0), 0);

  const rowStyle = (isHighlighted: boolean, isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.xs,
    padding: `${SPACING.xxs}px ${SPACING.sm}px`,
    minHeight: TAP,
    cursor: 'pointer',
    fontSize: FONT_SIZE.sm,
    borderRadius: RADIUS.sm,
    background: isHighlighted ? t.tintInfo : 'transparent',
    fontWeight: isSelected ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
    color: t.textPrimary,
  });

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: `${SPACING.xxs}px ${SPACING.xs}px`,
    ...(phone ? { minHeight: 44, display: 'inline-flex', alignItems: 'center', boxSizing: 'border-box' as const } : {}),
    borderRadius: '999px', // 자기 크기에 비례하는 필 모양 — AppFilterChips와 동일한 의도적 예외(CLAUDE.md §7).
    border: `1px solid ${active ? t.lineAccent : t.border}`,
    background: active ? t.tintInfo : t.bgRaised,
    color: active ? t.accent : t.textSecondary,
    fontSize: FONT_SIZE.xs,
    fontWeight: active ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  });

  // 확장팩 그룹 머리줄 — 접을 수 있으면 버튼(button 기본 테두리 · 글꼴을 지운다, 감사 #518).
  const groupHeaderStyle: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 1, width: '100%', boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: TAP,
    padding: `${SPACING.xs}px ${SPACING.sm}px`, margin: `${SPACING.xxs}px 0 0`,
    background: t.bgRaised, borderRadius: RADIUS.sm, border: 'none', font: 'inherit', textAlign: 'left',
    color: 'inherit', cursor: 'default', userSelect: 'none',
  };

  const typeChips: { key: TypeFilter; label: string }[] = [
    { key: 'ALL', label: `전체 ${options.length}` },
    { key: 'BOOSTER', label: `부스터팩 ${options.filter((o) => o.productType === 'BOOSTER').length}` },
    { key: 'STARTER', label: `스타터덱 ${options.filter((o) => o.productType === 'STARTER').length}` },
  ];

  const renderRow = (o: FilterOption) => {
    // 상품 등록 드롭다운: registeredCount가 오면 "미등록 N장 / 전부 등록됨" 배지를
    // 보여주고(선택 전에 어느 팩에 할 일이 남았는지 보이도록), meta는 발매일만 남긴다.
    const hasWorkload = o.registeredCount != null && o.count != null;
    const unregistered = hasWorkload ? Math.max((o.count ?? 0) - (o.registeredCount ?? 0), 0) : 0;
    const meta = hasWorkload ? (o.releaseDate ?? null) : formatMeta(o);
    const navIdx = flatIndexOf.get(o.value);
    return (
      <div
        key={o.value}
        role="button"
        onClick={() => pick(o.value)}
        style={rowStyle(navIdx != null && highlight === navIdx + allOffset, value === o.value)}
      >
        <span style={{
          fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: t.accent,
          background: t.tintInfo, borderRadius: RADIUS.sm, padding: '1px 5px', whiteSpace: 'nowrap',
        }}>
          {o.value}
        </span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
        {o.productType === 'STARTER' && (
          <span style={{
            fontSize: 10, color: t.textMuted, border: `1px solid ${t.border}`,
            borderRadius: RADIUS.sm, padding: '0 4px', whiteSpace: 'nowrap',
          }}>
            {PRODUCT_TYPE_LABEL.STARTER}
          </span>
        )}
        {hasWorkload && (
          <span style={{
            fontSize: FONT_SIZE.xs, whiteSpace: 'nowrap',
            fontWeight: unregistered > 0 ? FONT_WEIGHT.bold : FONT_WEIGHT.regular,
            color: unregistered > 0 ? t.accent : t.textMuted,
          }}>
            {unregistered > 0 ? `미등록 ${unregistered.toLocaleString()}장` : '전부 등록됨'}
          </span>
        )}
        {meta && <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, whiteSpace: 'nowrap' }}>{meta}</span>}
        {value === o.value && <span style={{ color: t.accent }}>✓</span>}
      </div>
    );
  };

  const panel = (
    // 휴대폰은 화면 폭 안으로 — 팝오버는 이 패널에 안쪽 여백(12 × 2)과 바깥 틀(12 × 2)을 더해 그리므로 화면 폭 − 화면 여백(16 × 2) − 48.
    // 360 고정이면 넘쳐서 오른쪽 맞춤으로 뒤집히며 왼쪽이 화면 밖으로 나갔다(감사 #518).
    <div style={{ width: phone ? 'calc(100vw - 80px)' : 360, maxWidth: 360 }} onKeyDown={handleKeyDown}>
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
        placeholder="확장팩 검색 — 코드 또는 이름"
        prefix={<SearchOutlined style={{ color: t.textMuted }} />}
        allowClear
        size={phone ? 'large' : undefined}
        style={{ marginBottom: SPACING.xs, minHeight: TAP }}
      />

      {typeFilterOn && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, flexWrap: 'wrap', marginBottom: SPACING.xs }}>
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>종류</span>
          {typeChips.map((c) => (
            <div
              key={c.key}
              role="button"
              onClick={() => { setTypeFilter(c.key); setHighlight(0); }}
              style={chipStyle(typeFilter === c.key)}
            >
              {c.label}
            </div>
          ))}
        </div>
      )}

      {!query.trim() && recentOptions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, flexWrap: 'wrap', marginBottom: SPACING.xs }}>
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>최근</span>
          {recentOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => pick(o.value)}
              style={{
                border: `1px solid ${t.border}`,
                background: t.bgRaised,
                borderRadius: '999px',
                padding: `${SPACING.xxs}px ${SPACING.xs}px`,
                minHeight: TAP,
                fontSize: FONT_SIZE.xs,
                color: t.textSecondary,
                cursor: 'pointer',
              }}
            >
              {o.value} {o.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ padding: SPACING.lg, textAlign: 'center', color: t.textMuted, fontSize: FONT_SIZE.sm }}>
          확장팩 목록을 불러오는 중...
        </div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto', borderTop: `1px solid ${t.borderSubtle}`, paddingTop: SPACING.xxs }}>
          {allowAll && (
            <div role="button" onClick={() => pick('ALL')} style={rowStyle(highlight === 0, value === 'ALL')}>
              <span style={{ flex: 1 }}>확장팩 전체</span>
              <span style={{ color: t.textMuted, fontSize: FONT_SIZE.xs }}>
                {totalCards > 0 ? `${totalCards.toLocaleString()}장` : options.length}
              </span>
            </div>
          )}

          {groups.map((g) => {
            const isCollapsed = collapsible && !!collapsed[g.prefix];
            const kinds = Array.from(new Set(g.items.map((o) => o.productType).filter(Boolean)))
              .map((k) => PRODUCT_TYPE_LABEL[k as 'BOOSTER' | 'STARTER']);
            const groupHeader = (
              <>
                <span style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs }}>
                  <span aria-hidden style={{
                    display: 'inline-block', fontSize: 10, color: t.textMuted,
                    opacity: collapsible ? 1 : 0,
                    transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                    transition: 'transform 0.12s ease',
                  }}>
                    ▾
                  </span>
                  <span style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, letterSpacing: 0.6, color: t.textSecondary }}>
                    {g.prefix}
                  </span>
                  {kinds.length > 0 && (
                    <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{kinds.join(' · ')}</span>
                  )}
                </span>
                <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{g.items.length}개</span>
              </>
            );
            return (
              <div key={g.prefix}>
                {/* 접을 수 있으면 진짜 버튼 — Tab 으로 들어와 Enter · Space 로 접고 펼치고, 펼침 상태를 읽어 준다(감사 #518). */}
                {collapsible ? (
                  <button
                    type="button"
                    className="app-focus-ring"
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleGroup(g.prefix)}
                    style={{ ...groupHeaderStyle, cursor: 'pointer' }}
                  >
                    {groupHeader}
                  </button>
                ) : (
                  <div style={groupHeaderStyle}>{groupHeader}</div>
                )}
                {!isCollapsed && g.items.map(renderRow)}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div style={{ padding: SPACING.lg, textAlign: 'center', color: t.textMuted, fontSize: FONT_SIZE.sm }}>
              검색 결과가 없습니다
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={panel}
      styles={{ content: { padding: SPACING.sm } }}
    >
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.xs,
          height: phone ? 44 : 32,
          padding: `0 ${SPACING.sm}px`,
          border: `1px solid ${open ? t.accent : t.border}`,
          background: t.bgSurface,
          borderRadius: RADIUS.md,
          fontSize: FONT_SIZE.sm,
          color: t.textPrimary,
          cursor: 'pointer',
          width: style?.width ?? 200,
          ...style,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ color: t.textMuted, fontSize: 10 }}>▾</span>
      </button>
    </Popover>
  );
};
